import { Object, Optional, String, Static, Number, Array, Boolean, Omit } from '@sinclair/typebox';

export const DapiSpecialServiceRequests = Object({
  id: String(),
  code: String(),
  airlineCode: String(),
  statusCode: String(),
  quantity: Number(),
  freeText: String(),
  flightIds: Array(String()),
  travelerIds: Array(String()),
});
export type DapiSpecialServiceRequests = Static<typeof DapiSpecialServiceRequests>;

export const DapiRetrieveReservationFlight = Object({
  id: String(),
  cabin: String(),
  bookingClass: String(),
  statusCode: String(),
  fareFamilyCode: Optional(String()),
  connectionTime: Optional(Number()),
});
export type DapiRetrieveReservationFlight = Static<typeof DapiRetrieveReservationFlight>;

const DisruptionStatusData = Object({
  locationCode: String(),
  sameAirport: Boolean(),
  sameCity: Boolean(),
  sameDay: Boolean(),
  deltaTime: Number(),
});
type DisruptionStatusData = Static<typeof DisruptionStatusData>;

export const DapiRetrieveReservationBound = Object({
  airBoundId: String(),
  originLocationCode: String(),
  destinationLocationCode: String(),
  flights: Array(DapiRetrieveReservationFlight),
  duration: Number(),
  disruptionStatus: Optional(
    Object({
      boundStatus: String(),
      originalFlights: Array(DapiRetrieveReservationFlight),
      departure: DisruptionStatusData,
      arrival: DisruptionStatusData,
      connection: Object({ status: String(), delta: Number(), sameViaPoint: Boolean(), deltaTime: Number() }),
      duration: Number(),
    })
  ),
});

export type DapiRetrieveReservationBound = Static<typeof DapiRetrieveReservationBound>;

export const DapiSegmentLocation = Object({
  locationCode: String(),
  dateTime: String(),
  terminal: Optional(String()),
});
export type DapiSegmentLocation = Static<typeof DapiSegmentLocation>;

export const DapiDictionariesLocation = Object({
  type: Optional(String()),
  airportName: Optional(String()),
  cityCode: String(),
  countryCode: Optional(String()),
  cityName: Optional(String()),
  timeZone: Optional(String()),
});
export type DapiDictionariesLocation = Static<typeof DapiDictionariesLocation>;

export const DapiFareInfos = Array(
  Object({
    id: String(),
    fareClass: String(),
    fareFamilyCode: String(),
    couponStatus: String(),
    flight: Object({
      marketingAirlineCode: String(),
      marketingFlightNumber: String(),
      departure: DapiSegmentLocation,
      arrival: Object({ locationCode: String() }),
    }),
  })
);
export type DapiTravelDocumentFareInfos = Static<typeof DapiFareInfos>;

export const DapiTravelDocuments = Object({
  flightIds: Array(String()),
  travelerIds: Array(String()),
  id: String(),
  documentType: String(),
  endorsement: Optional(String()),
  paymentTransactions: Array(
    Object({
      paymentMethod: Object({ id: String(), paymentType: String() }),
      amount: Object({ value: Number(), currencyCode: String() }),
    })
  ),
  fareInfos: DapiFareInfos,
  creation: Object({
    triggerEventName: String(),
    localDateTime: String(),
    office: Object({ officeId: String(), location: DapiDictionariesLocation }),
    location: DapiDictionariesLocation,
  }),
  areCouponsAndSegmentsInSync: Boolean(),
  status: String(),
});
export type DapiTravelDocuments = Static<typeof DapiTravelDocuments>;

export const DapiDictionariesFlight = Object({
  departure: DapiSegmentLocation,
  arrival: DapiSegmentLocation,
  marketingAirlineCode: String(),
  operatingAirlineCode: Optional(String()),
  operatingAirlineName: Optional(String()),
  marketingFlightNumber: String(),
  operatingAirlineFlightNumber: Optional(String()),
  duration: Number(),
  aircraftCode: String(),
  isOpenSegment: Optional(Boolean()),
  isInformational: Boolean(),
  secureFlightIndicator: Boolean(),
  meals: Object({ bookingClass: String(), mealCodes: Array(String()) }),
  flightStatus: String(),
  aircraftConfigurationVersion: String(),
});
export type DapiDictionariesFlight = Static<typeof DapiDictionariesFlight>;

const DapiMessage = Object({
  code: Optional(String()),
  title: String(),
  status: Optional(String()),
});
export type DapiMessage = Static<typeof DapiMessage>;

export const DapiError = DapiMessage;
export type DapiError = Static<typeof DapiError>;

export const DapiWarning = Omit(DapiMessage, ['status']);
export type DapiWarning = Static<typeof DapiWarning>;

export const DapiAir = Object({
  bounds: Array(DapiRetrieveReservationBound),
  fareInfos: Array(Object({ fareClass: String(), travelerIds: Array(String()), flightIds: Array(String()) })),
  freeCheckedBaggageAllowanceItems: Array(
    Object({
      details: Object({ type: String(), quantity: Number() }),
      flightIds: Array(String()),
      travelersIds: Optional(Array(String())),
    })
  ),
});
export type DapiAir = Static<typeof DapiAir>;

export const DapiService = Object({
  id: String(),
  descriptions: Array(Object({ type: String(), content: String() })),
  quantity: Number(),
  flightIds: Array(String()),
  statusCode: String(),
  parameters: Array(Object({ code: String(), value: String() })),
  travelerId: String(),
  isChargeable: Boolean(),
});
export type DapiService = Static<typeof DapiService>;

export const DapiContact = Object({
  id: String(),
  category: String(),
  contactType: String(),
  deviceType: Optional(String()),
  purpose: String(),
  countryPhoneExtension: Optional(String()),
  number: Optional(String()),
  address: Optional(String()),
  freeFlowText: Optional(String()),
});
export type DapiContact = Static<typeof DapiContact>;
